import { useEffect } from 'react';

/**
 * Custom hook to dynamically inject metadata, canonical links, open graph tags,
 * and schema.org JSON-LD scripts on the client side.
 * 
 * @param {Object} seoConfig Configuration parameters for SEO metadata.
 * @param {string} seoConfig.title Document title.
 * @param {string} seoConfig.description Meta description.
 * @param {string} seoConfig.canonical Canonical page URL.
 * @param {Object} seoConfig.schema JSON-LD Schema.org object.
 * @param {Object} seoConfig.openGraph Additional Open Graph tag mappings.
 */
export const useSEO = ({ title, description, canonical, schema, openGraph = {} }) => {
  useEffect(() => {
    // 1. Title
    const originalTitle = document.title;
    if (title) {
      document.title = title;
    }

    // 2. Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc ? metaDesc.getAttribute('content') : '';
    if (description) {
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
    }

    // 3. Canonical Link
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const originalCanonical = linkCanonical ? linkCanonical.getAttribute('href') : '';
    if (canonical) {
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    }

    // 4. Schema JSON-LD Script
    let scriptSchema = document.querySelector('script[type="application/ld+json"]#seo-schema');
    if (schema) {
      if (!scriptSchema) {
        scriptSchema = document.createElement('script');
        scriptSchema.setAttribute('type', 'application/ld+json');
        scriptSchema.setAttribute('id', 'seo-schema');
        document.head.appendChild(scriptSchema);
      }
      scriptSchema.textContent = JSON.stringify(schema);
    }

    // 5. Open Graph / Social tags
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:url': canonical,
      ...openGraph
    };

    const originalOgValues = {};
    Object.entries(ogTags).forEach(([key, value]) => {
      if (value) {
        const isTwitter = key.startsWith('twitter:');
        const selector = isTwitter ? `meta[name="${key}"]` : `meta[property="${key}"]`;
        let metaTag = document.querySelector(selector);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute(isTwitter ? 'name' : 'property', key);
          document.head.appendChild(metaTag);
        }
        originalOgValues[key] = metaTag.getAttribute('content') || '';
        metaTag.setAttribute('content', value);
      }
    });

    return () => {
      // Clean up / Restore original tags on unmount
      document.title = originalTitle;
      
      if (metaDesc) {
        if (originalDesc) {
          metaDesc.setAttribute('content', originalDesc);
        } else {
          metaDesc.remove();
        }
      }

      if (linkCanonical) {
        if (originalCanonical) {
          linkCanonical.setAttribute('href', originalCanonical);
        } else {
          linkCanonical.remove();
        }
      }

      if (scriptSchema) {
        scriptSchema.remove();
      }

      Object.entries(originalOgValues).forEach(([key, value]) => {
        const isTwitter = key.startsWith('twitter:');
        const selector = isTwitter ? `meta[name="${key}"]` : `meta[property="${key}"]`;
        const metaTag = document.querySelector(selector);
        if (metaTag) {
          if (value) {
            metaTag.setAttribute('content', value);
          } else {
            metaTag.remove();
          }
        }
      });
    };
  }, [title, description, canonical, schema, openGraph]);
};
